import { BreakEven } from '../../types/dealers';
import { discountError } from './dealerFormat';

/** Hundredths of a percent, so the margin is exact ("12.34" → 1234). */
const hundredths = (s: string) => Math.round(Number(s) * 100);

/**
 * BR-36 — the margin per AED 1,000 of credit at the lowest live garage commission, the same figure
 * orders' `BreakEvenService.hint` returns: (lowest − discount) × 10, 2 dp. Display only.
 */
export function breakEvenFor(discountPct: string, lowestCommissionPct: string): BreakEven | null {
  if (discountError(discountPct) || !Number.isFinite(Number(lowestCommissionPct))) return null;
  const diff = hundredths(lowestCommissionPct) - hundredths(discountPct);
  return {
    lowestCommissionPct,
    marginPer1000Aed: (diff / 10).toFixed(2),
    warning: diff <= 0,
  };
}

interface BreakEvenHintProps {
  /** The discount being typed. */
  discountPct: string;
  /** From `GET /break-even`: undefined while loading, null when the rate is unknown. */
  lowestCommissionPct: string | null | undefined;
  /** The hint the last save returned (PATCH/POST `breakEven`), shown while the field is unchanged. */
  saved?: BreakEven | null;
  /** The admin holds finance-config (or is a super admin), so a discount at or above break-even saves. */
  canSaveAboveBreakEven: boolean;
}

/**
 * The inline amber callout next to the discount field (requirements §5, AC-6). A discount at or
 * above the lowest live commission warns with the margin; saving it needs finance-config as well as
 * `dealers` (Permissions row). With the rate unknown the backend fails closed: any change needs
 * finance-config.
 */
export default function BreakEvenHint({ discountPct, lowestCommissionPct, saved, canSaveAboveBreakEven }: BreakEvenHintProps) {
  if (lowestCommissionPct === undefined && !saved) return null;

  if (lowestCommissionPct === null && !saved?.lowestCommissionPct) {
    return (
      <div className="dealer-break-even info" role="note" data-testid="break-even-unknown">
        The lowest live garage commission is unknown right now, so the break-even cannot be shown.
        {!canSaveAboveBreakEven && ' Changing the discount needs an admin with finance configuration access.'}
      </div>
    );
  }

  const lowest = lowestCommissionPct ?? saved?.lowestCommissionPct ?? null;
  const hint = lowest ? breakEvenFor(discountPct, lowest) : saved ?? null;
  if (!hint || !hint.lowestCommissionPct) return null;

  const at = `${Number(discountPct)}%`;
  const garage = `${Number(hint.lowestCommissionPct)}%`;
  const margin = `AED ${hint.marginPer1000Aed}`;

  if (!hint.warning) {
    return (
      <div className="dealer-break-even ok" data-testid="break-even-ok">
        Break-even: the lowest live garage commission is {garage}. At {at} Garagat keeps {margin} per AED 1,000 of credit at that garage.
      </div>
    );
  }
  return (
    <div className="dealer-break-even warning" role="alert" data-testid="break-even-warning">
      <strong>At or above break-even.</strong>{' '}
      At {at} the margin at a {garage} garage is {margin} per AED 1,000 of credit. A promo code can still stack on top of the dealer discount.{' '}
      {canSaveAboveBreakEven
        ? 'You can save it (warning only).'
        : 'Only an admin with finance configuration access can save this discount.'}
    </div>
  );
}
